import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, API } from "../api";
import { Send, Square, MessageSquare, Sparkles, CheckCircle2, Clock, ChevronRight, AlertCircle, Mic, MicOff, Volume2, Type as TypeIcon } from "lucide-react";

/**
 * AI Role-Play page (participant) — two flows:
 *  1) /participant/roleplay/:assignmentId            → list exercises + status
 *  2) /participant/roleplay/:assignmentId/:exerciseId → live chat
 */
export default function RolePlayHub() {
  const { assignmentId, exerciseId } = useParams();
  if (exerciseId) return <RolePlayChat assignmentId={assignmentId} exerciseId={exerciseId} />;
  return <ExercisesList assignmentId={assignmentId} />;
}

function ExercisesList({ assignmentId }) {
  const [exercises, setExercises] = useState(null);
  const [sessions, setSessions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/roleplay/exercises"),
      api.get(`/roleplay/sessions?assignment_id=${assignmentId}`),
    ]).then(([e, s]) => {
      setExercises(e.data);
      setSessions(s.data);
    });
  }, [assignmentId]);

  if (!exercises) return <div className="text-center text-[#878787] py-20 text-sm">Loading exercises…</div>;

  const statusOf = (exId) => {
    const s = sessions.find((x) => x.exercise_id === exId);
    if (!s) return "pending";
    return s.status; // active | ended | scored
  };

  return (
    <div className="space-y-10 fade-in">
      <div>
        <div className="overline mb-2 !text-[#EE2737]">AI ASSESSMENT ENGINE · LIVE CONVERSATION</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">AI Role-Play Exercises</h1>
        <p className="text-[#525252] text-sm mt-2 max-w-3xl leading-relaxed">
          You will hold two live conversations with an AI character — a customer CIO, and then a channel partner.
          Each conversation runs for up to 10 turns (~10 minutes). Stay in role. Treat it like a real meeting. There are no right answers — the AI scores you on your reasoning, structure, and executive presence, mapped to the 7-competency framework.
        </p>
      </div>

      <div className="bg-[#FEF3C7] border-l-4 border-[#D97706] p-5 max-w-3xl">
        <div className="flex gap-3">
          <AlertCircle size={20} className="text-[#D97706] flex-shrink-0 mt-0.5" />
          <div className="text-[13px] text-[#92400E] leading-relaxed">
            <strong className="font-bold">One attempt per exercise.</strong> Once you click <em>End conversation</em>, your transcript is locked and AI-scored against the BARS rubric. Do the digital assessment first if you haven't yet — your role-play insights become part of the final report.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {exercises.map((e, i) => {
          const status = statusOf(e.id);
          const done = status === "scored";
          return (
            <div key={e.id} className="card p-6 flex flex-col" data-testid={`exercise-card-${e.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center rounded-sm">
                  <span className="text-white font-heading font-black">E{i + 1}</span>
                </div>
                <span className={`badge ${done ? "badge-success" : status === "active" ? "badge-warning" : "badge-neutral"}`}>
                  {done ? <><CheckCircle2 size={11} /> Scored</> : status === "active" ? "In progress" : "Not started"}
                </span>
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">{e.name}</h3>
              <p className="text-[13px] text-[#525252] leading-relaxed mb-4 flex-1">{e.short_desc}</p>
              <div className="flex items-center gap-4 text-xs text-[#878787] mb-4">
                <span className="flex items-center gap-1"><Clock size={12} />{e.duration_min} min</span>
                <span className="flex items-center gap-1"><MessageSquare size={12} />{e.max_turns} turns max</span>
              </div>
              <div className="overline mb-1">COMPETENCIES MEASURED</div>
              <div className="flex flex-wrap gap-1 mb-5">
                {e.competencies.map((c) => (
                  <span key={c} className="text-[10px] uppercase tracking-wider px-2 py-1 bg-[#F3F4F6] text-[#525252] font-semibold">
                    {c.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
              <button
                onClick={() => !done && navigate(`/participant/roleplay/${assignmentId}/${e.id}`)}
                disabled={done}
                className={`btn ${done ? "btn-secondary" : "btn-primary"} !w-full justify-center`}
                data-testid={`start-roleplay-${e.id}`}
              >
                {done ? "Already completed" : (<><Sparkles size={14} />Start live conversation</>)}
              </button>
            </div>
          );
        })}
      </div>

      <div className="pt-4 border-t border-[#E5E7EB]">
        <button onClick={() => navigate("/participant")} className="btn btn-ghost !px-2" data-testid="back-dashboard">← Back to dashboard</button>
      </div>
    </div>
  );
}

function RolePlayChat({ assignmentId, exerciseId }) {
  const [exercise, setExercise] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [scores, setScores] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("text"); // 'text' | 'voice'
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState(() => {
    try { return localStorage.getItem("vi_roleplay_lang") || "en-IN"; } catch { return "en-IN"; }
  });
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);

  // Load exercise metadata + supported languages on mount.
  // Do NOT auto-start the session — wait for the user to pick a language.
  useEffect(() => {
    const init = async () => {
      try {
        const [ex, langs] = await Promise.all([
          api.get("/roleplay/exercises"),
          api.get("/roleplay/languages"),
        ]);
        const myEx = ex.data.find((e) => e.id === exerciseId);
        setExercise(myEx);
        setLanguages(langs.data);
      } catch (e) {
        setErr(formatApiError(e));
      }
    };
    init();
  }, [assignmentId, exerciseId]);

  const startSession = async () => {
    if (starting || session) return;
    setStarting(true);
    setErr("");
    try {
      try { localStorage.setItem("vi_roleplay_lang", language); } catch { /* ignore */ }
      const s = await api.post("/roleplay/start", {
        assignment_id: assignmentId,
        exercise_id: exerciseId,
        language,
      });
      setSession(s.data);
      setMessages(s.data.messages);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, transcribing]);

  // Speak opening line when entering voice mode the first time
  const playedOpeningRef = useRef(false);
  useEffect(() => {
    const speakOpening = async () => {
      if (mode !== "voice" || !session || playedOpeningRef.current) return;
      playedOpeningRef.current = true;
      try {
        const form = new FormData();
        form.append("session_id", session.id);
        const r = await fetch(`${API}/roleplay/speak-opening`, {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("vi_token")}` },
          body: form,
        });
        const data = await r.json();
        if (data.audio_base64) playAudio(data.audio_base64);
      } catch (e) { /* ignore */ }
    };
    speakOpening();
  }, [mode, session]);

  const playAudio = (b64) => {
    if (!b64) return;
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    const audio = new Audio(`data:audio/wav;base64,${b64}`);
    audioPlayerRef.current = audio;
    audio.play().catch(() => {});
  };

  const userTurns = messages.filter((m) => m.role === "user").length;
  const maxTurns = exercise?.max_turns || 10;
  const turnsLeft = maxTurns - userTurns;
  const progress = (userTurns / maxTurns) * 100;

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = { role: "user", content: input.trim(), turn: userTurns + 1 };
    setMessages([...messages, userMsg]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/roleplay/turn", { session_id: session.id, message: userMsg.content });
      setMessages((m) => [...m, { role: "assistant", content: r.data.ai_message, turn: r.data.turn }]);
      if (r.data.should_end) {
        setTimeout(() => endSession(), 600);
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        await uploadVoiceTurn(blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      setErr("Microphone access denied. Please allow mic permissions and try again.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const uploadVoiceTurn = async (blob) => {
    setTranscribing(true);
    setErr("");
    try {
      const form = new FormData();
      form.append("session_id", session.id);
      form.append("audio", blob, "turn.webm");
      const r = await fetch(`${API}/roleplay/voice-turn`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("vi_token")}` },
        body: form,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Voice turn failed");
      setMessages((m) => [
        ...m,
        { role: "user", content: data.user_transcript, turn: data.turn },
        { role: "assistant", content: data.ai_message, turn: data.turn },
      ]);
      playAudio(data.ai_audio_base64);
      if (data.should_end) setTimeout(() => endSession(), 1500);
    } catch (e) {
      setErr(e.message || "Voice turn failed");
    } finally {
      setTranscribing(false);
    }
  };

  const endSession = async () => {
    if (ending) return;
    setEnding(true);
    try {
      const r = await api.post("/roleplay/end", { session_id: session.id });
      setScores(r.data.scores);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setEnding(false);
    }
  };

  if (err && !session) return (
    <div className="max-w-3xl mx-auto fade-in">
      <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3 rounded-sm">{err}</div>
      <button onClick={() => navigate(`/participant/roleplay/${assignmentId}`)} className="btn btn-ghost mt-4">← Back</button>
    </div>
  );
  if (!exercise) return <div className="text-center text-[#878787] py-20 text-sm">Loading exercise…</div>;

  // Pre-start screen: language picker (before session is created)
  if (!session) {
    return (
      <div className="max-w-2xl mx-auto fade-in py-6" data-testid="roleplay-language-picker">
        <div className="overline mb-2 !text-[#EE2737]">LIVE ROLE-PLAY · LANGUAGE</div>
        <h1 className="font-heading font-black text-3xl tracking-tighter mb-2">{exercise.name}</h1>
        <p className="text-[13px] text-[#525252] leading-relaxed mb-8 max-w-xl">
          Pick the language you want to converse in. The AI will speak and understand in this language, across all major Indian languages.
        </p>

        <div className="overline mb-3">CONVERSATION LANGUAGE</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-8">
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                setLanguage(l.code);
                try { localStorage.setItem("vi_roleplay_lang", l.code); } catch { /* ignore */ }
              }}
              className={`px-4 py-3 border text-left transition-all ${
                language === l.code
                  ? "border-[#EE2737] bg-[#FEE2E2] text-[#0A0A0A]"
                  : "border-[#E5E7EB] bg-white text-[#525252] hover:border-[#0A0A0A]"
              }`}
              data-testid={`lang-${l.code}`}
            >
              <div className="text-[11px] uppercase tracking-wider font-semibold opacity-70">{l.code}</div>
              <div className="text-sm font-heading font-bold">{l.label}</div>
            </button>
          ))}
        </div>

        <div className="bg-[#FEF3C7] border-l-4 border-[#D97706] p-4 text-[12px] text-[#92400E] leading-relaxed mb-8">
          <strong>Heads up:</strong> Once you click Begin, the language is locked for this session and your one attempt at this exercise begins.
        </div>

        <div className="flex gap-3">
          <button onClick={() => navigate(`/participant/roleplay/${assignmentId}`)} className="btn btn-ghost" data-testid="back-from-lang">← Cancel</button>
          <button onClick={startSession} disabled={starting} className="btn btn-primary" data-testid="begin-roleplay">
            {starting ? "Starting…" : (<><Sparkles size={14} /> Begin conversation in {languages.find((l) => l.code === language)?.label || "English"}</>)}
          </button>
        </div>
      </div>
    );
  }

  if (scores) return <ScoringScreen exercise={exercise} scores={scores} onDone={() => navigate(`/participant/roleplay/${assignmentId}`)} />;

  return (
    <div className="max-w-3xl mx-auto fade-in">
      {/* Header bar */}
      <div className="sticky top-16 bg-[#F8F9FA] -mx-4 sm:-mx-8 px-4 sm:px-8 py-4 z-10 border-b border-[#E5E7EB]">
        <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
          <div className="overline">LIVE ROLE-PLAY · {exercise.name} · <span className="!text-[#EE2737]">{languages.find((l) => l.code === session.language)?.label || "English"}</span></div>
          <div className="flex items-center gap-2">
            {/* Mode toggle */}
            <div className="flex border border-[#E5E7EB] bg-white rounded-sm overflow-hidden" data-testid="mode-toggle">
              <button onClick={() => setMode("text")}
                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 ${mode === "text" ? "bg-[#0A0A0A] text-white" : "text-[#525252]"}`}
                data-testid="mode-text"
              ><TypeIcon size={12} /> Text</button>
              <button onClick={() => setMode("voice")}
                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 ${mode === "voice" ? "bg-[#EE2737] text-white" : "text-[#525252]"}`}
                data-testid="mode-voice"
              ><Mic size={12} /> Voice</button>
            </div>
            <span className="text-[11px] uppercase tracking-wider text-[#878787] font-semibold">
              Turn {userTurns} / {maxTurns}
            </span>
            <button onClick={endSession} disabled={userTurns < 2 || ending} className="btn btn-secondary !py-1.5 !px-3 !text-xs" data-testid="end-conversation">
              {ending ? "Scoring…" : <><Square size={11} /> End</>}
            </button>
          </div>
        </div>
        <div className="progress-bar"><div style={{ width: `${progress}%` }} /></div>
      </div>

      {/* Chat */}
      <div ref={scrollRef} className="py-8 space-y-5 min-h-[55vh] max-h-[60vh] overflow-y-auto">
        {messages.map((m, i) => (
          <ChatBubble key={i} role={m.role} content={m.content} />
        ))}
        {(sending || transcribing) && (
          <ChatBubble role="assistant" content={
            <span className="inline-flex gap-1 items-center">
              {transcribing && <span className="text-[11px] uppercase tracking-wider text-[#878787] font-semibold mr-2">Transcribing…</span>}
              <Dot /><Dot delay={150} /><Dot delay={300} />
            </span>
          } />
        )}
      </div>

      {/* Input — TEXT mode */}
      {mode === "text" && (
        <div className="sticky bottom-0 bg-[#F8F9FA] -mx-4 sm:-mx-8 px-4 sm:px-8 py-4 border-t border-[#E5E7EB]">
          <div className="flex gap-3 items-end">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type your response… (Shift+Enter for new line)"
              rows={2}
              className="input font-body flex-1 resize-none"
              disabled={sending || turnsLeft <= 0}
              data-testid="roleplay-input"
            />
            <button onClick={send} disabled={!input.trim() || sending || turnsLeft <= 0}
              className="btn btn-primary !py-3 !px-4" data-testid="roleplay-send">
              <Send size={14} />
            </button>
          </div>
          <div className="text-[11px] text-[#878787] mt-2 flex justify-between">
            <span>{turnsLeft} turn{turnsLeft !== 1 ? "s" : ""} remaining · stay in role · click End when finished</span>
            <span className="font-semibold">{input.split(/\s+/).filter(Boolean).length} words</span>
          </div>
        </div>
      )}

      {/* Input — VOICE mode */}
      {mode === "voice" && (
        <div className="sticky bottom-0 bg-[#F8F9FA] -mx-4 sm:-mx-8 px-4 sm:px-8 py-6 border-t border-[#E5E7EB]">
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={recording ? stopRecording : startRecording}
              disabled={transcribing || turnsLeft <= 0}
              className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                recording ? "bg-[#EE2737] animate-pulse" : transcribing ? "bg-[#878787] cursor-not-allowed" : "bg-[#0A0A0A] hover:bg-[#EE2737]"
              } disabled:opacity-50`}
              data-testid="mic-button"
            >
              {recording ? <Square size={28} className="text-white fill-white" /> : <Mic size={28} className="text-white" />}
            </button>
            <div className="text-center">
              <div className="font-heading font-bold text-sm">
                {recording ? "Recording… tap to stop" : transcribing ? "Transcribing & generating response…" : turnsLeft <= 0 ? "Max turns reached — click End" : "Tap to speak"}
              </div>
              <div className="text-[11px] text-[#878787] mt-1 flex items-center justify-center gap-1">
                <Volume2 size={11} /> AI responds in voice · {turnsLeft} turn{turnsLeft !== 1 ? "s" : ""} remaining
              </div>
            </div>
          </div>
        </div>
      )}
      {err && <div className="mt-3 bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3 rounded-sm">{err}</div>}
    </div>
  );
}

function ChatBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] ${isUser ? "" : "flex gap-3 items-start"}`}>
        {!isUser && (
          <div className="w-8 h-8 bg-[#0A0A0A] text-white flex items-center justify-center font-heading font-black text-xs flex-shrink-0">AI</div>
        )}
        <div>
          {!isUser && <div className="overline mb-1">CHARACTER</div>}
          <div className={`px-4 py-3 text-[14px] leading-relaxed ${
            isUser
              ? "bg-[#EE2737] text-white rounded-sm"
              : "bg-white border border-[#E5E7EB] rounded-sm text-[#0A0A0A]"
          }`}>{content}</div>
        </div>
      </div>
    </div>
  );
}

function Dot({ delay = 0 }) {
  return (
    <span
      className="inline-block w-1.5 h-1.5 bg-[#878787] rounded-full animate-pulse"
      style={{ animationDelay: `${delay}ms` }}
    />
  );
}

function ScoringScreen({ exercise, scores, onDone }) {
  const compScores = scores.scores || {};
  const justifications = scores.justifications || {};
  return (
    <div className="max-w-3xl mx-auto fade-in py-12">
      <div className="overline mb-2 !text-[#008A3D]">AI ASSESSOR · SCORING COMPLETE</div>
      <h2 className="font-heading font-black text-3xl tracking-tighter mb-2">Your performance — {exercise.name}</h2>
      <p className="text-[#525252] text-sm mb-8 leading-relaxed">
        These scores feed into your final competency report (alongside SJT, MCQ, self-rating, and 360° feedback).
        Each score is anchored to specific moments in your conversation.
      </p>

      {scores.overall_summary && (
        <div className="card p-6 mb-8 border-l-4 !border-l-[#0A0A0A]">
          <div className="overline mb-2">OVERALL FEEDBACK</div>
          <p className="text-[15px] leading-relaxed text-[#0A0A0A] italic">"{scores.overall_summary}"</p>
        </div>
      )}

      <div className="space-y-3 mb-10">
        {Object.entries(compScores).map(([k, sc]) => (
          <div key={k} className="card p-5" data-testid={`rp-score-${k}`}>
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-heading font-bold text-base">{k.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</h4>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map((lvl) => (
                    <div key={lvl} className={`w-6 h-6 border ${lvl <= sc ? "bg-[#EE2737] border-[#EE2737]" : "bg-white border-[#E5E7EB]"}`} />
                  ))}
                </div>
                <span className="font-heading font-black text-xl text-[#EE2737]">{sc}</span>
              </div>
            </div>
            <p className="text-[13px] text-[#525252] italic leading-relaxed">"{justifications[k] || "—"}"</p>
          </div>
        ))}
      </div>

      <button onClick={onDone} className="btn btn-primary" data-testid="roleplay-done">
        Continue <ChevronRight size={14} />
      </button>
    </div>
  );
}
